import {
  Box,
  Button,
  Flex,
  FlexItem,
  H4,
  Link,
  Text,
} from '@bigcommerce/big-design';
import { OpenInNewIcon } from '@bigcommerce/big-design-icons';

import type { Location } from './types';

interface BigCommerceInfoTabProps {
  location: Location;
}

/**
 * Format operating hours for display
 */
function formatHours(open?: string, close?: string): string {
  if (!open || !close) return 'Closed';
  return `${open} - ${close}`;
}

/**
 * Display read-only BigCommerce location data
 * These fields are managed in BC admin, not in Kynetik
 */
export function BigCommerceInfoTab({ location }: BigCommerceInfoTabProps) {
  const address = location.address;
  const hasAddress = address?.address1 || address?.city;
  const hasOperatingHours = location.operating_hours &&
    Object.values(location.operating_hours).some(h => h !== null);

  return (
    <Box>
      <Flex justifyContent="space-between" alignItems="center" marginBottom="medium">
        <Text color="secondary">
          This information is managed in BigCommerce and is read-only here.
        </Text>
        {location.bcManageUrl && (
          <Button
            variant="secondary"
            iconRight={<OpenInNewIcon />}
            onClick={() => window.open(location.bcManageUrl, '_blank')}
          >
            Edit in BigCommerce
          </Button>
        )}
      </Flex>

      {/* Basic Info */}
      <Box marginBottom="large">
        <H4>Basic Information</H4>
        <Box backgroundColor="secondary10" padding="medium" borderRadius="normal">
          <Flex flexDirection="column" flexGap="small">
            <InfoRow label="Location Code" value={location.code} />
            <InfoRow label="Name" value={location.label} />
            {location.description && (
              <InfoRow label="Description" value={location.description} />
            )}
            <InfoRow
              label="Status"
              value={location.enabled ? 'Active' : 'Inactive'}
            />
            {location.type_id && (
              <InfoRow
                label="Type"
                value={location.type_id === 'PHYSICAL' ? 'Physical Store' : location.type_id}
              />
            )}
            {location.time_zone && (
              <InfoRow label="Timezone" value={location.time_zone} />
            )}
          </Flex>
        </Box>
      </Box>

      {/* Contact Info */}
      {(location.phone || location.email) && (
        <Box marginBottom="large">
          <H4>Contact Information</H4>
          <Box backgroundColor="secondary10" padding="medium" borderRadius="normal">
            <Flex flexDirection="column" flexGap="small">
              {location.phone && (
                <InfoRow label="Phone" value={location.phone} />
              )}
              {location.email && (
                <InfoRow label="Email">
                  <Link href={`mailto:${location.email}`}>{location.email}</Link>
                </InfoRow>
              )}
            </Flex>
          </Box>
        </Box>
      )}

      {/* Address */}
      {hasAddress && (
        <Box marginBottom="large">
          <H4>Address</H4>
          <Box backgroundColor="secondary10" padding="medium" borderRadius="normal">
            <Text>
              {address?.address1}
              {address?.address2 && <><br />{address.address2}</>}
              <br />
              {address?.city}, {address?.state} {address?.zip}
              <br />
              {address?.country}
            </Text>
            {location.latitude && location.longitude && (
              <Box marginTop="small">
                <Link
                  href={`https://www.google.com/maps?q=${location.latitude},${location.longitude}`}
                  target="_blank"
                >
                  View on Google Maps
                </Link>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Operating Hours */}
      {hasOperatingHours && (
        <Box marginBottom="large">
          <H4>Operating Hours</H4>
          <Box backgroundColor="secondary10" padding="medium" borderRadius="normal">
            <Flex flexDirection="column" flexGap="xSmall">
              <InfoRow label="Sunday" value={formatHours(location.operating_hours?.sunday?.open, location.operating_hours?.sunday?.close)} />
              <InfoRow label="Monday" value={formatHours(location.operating_hours?.monday?.open, location.operating_hours?.monday?.close)} />
              <InfoRow label="Tuesday" value={formatHours(location.operating_hours?.tuesday?.open, location.operating_hours?.tuesday?.close)} />
              <InfoRow label="Wednesday" value={formatHours(location.operating_hours?.wednesday?.open, location.operating_hours?.wednesday?.close)} />
              <InfoRow label="Thursday" value={formatHours(location.operating_hours?.thursday?.open, location.operating_hours?.thursday?.close)} />
              <InfoRow label="Friday" value={formatHours(location.operating_hours?.friday?.open, location.operating_hours?.friday?.close)} />
              <InfoRow label="Saturday" value={formatHours(location.operating_hours?.saturday?.open, location.operating_hours?.saturday?.close)} />
            </Flex>
          </Box>
        </Box>
      )}

      {/* Timestamps */}
      {(location.created_at || location.updated_at) && (
        <Box marginBottom="medium">
          <Text color="secondary" marginBottom="none">
            {location.created_at && `Created: ${new Date(location.created_at).toLocaleDateString()}`}
            {location.created_at && location.updated_at && ' • '}
            {location.updated_at && `Updated: ${new Date(location.updated_at).toLocaleDateString()}`}
          </Text>
        </Box>
      )}
    </Box>
  );
}

/**
 * Helper component for displaying label/value pairs
 */
function InfoRow({ label, value, children }: { label: string; value?: string; children?: React.ReactNode }) {
  return (
    <Flex>
      <FlexItem style={{ width: '140px' }}>
        <Text bold marginBottom="none">{label}:</Text>
      </FlexItem>
      <FlexItem>
        {children || <Text marginBottom="none">{value || '-'}</Text>}
      </FlexItem>
    </Flex>
  );
}
